// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Group, Skeleton, Space, Text } from '@mantine/core';
import { IconPlugConnectedX } from '@tabler/icons';
import { FC, useContext, useEffect, useMemo } from 'react';
import { useMailOutboundConfigs } from '../../../Service/hooks/useMailOutboundConfigs';
import { MailOutBoundConfigDTO } from '../../../Types/MailT';
import { useImapStore } from '../../../Service/hooks/useImapStore';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import InboxIcon from '../../../Atoms/InboxIcon';
import NavigationLink from '../../../Atoms/Navigation/NavigationLink';
import DefaultFolder from './DefaultFolderNav';

const MailOutboundConfigNav: FC<{ closeNavBar: () => void }> = ({ closeNavBar }) => {
  const { loading, mailOutboundConfigs } = useMailOutboundConfigs(true);
  const { role, userId } = useJwtClaims();
  const { bookitupTeam } = useContext(ProfileContext);
  const { ensureSharesLoaded, loading: loadingProfileShares, shares, sharesLoaded } = bookitupTeam;
  const isTenant = role === 'TENANT';

  useEffect(() => {
    if (isTenant) {
      ensureSharesLoaded().catch(() => undefined);
    }
  }, [ensureSharesLoaded, isTenant]);

  const visibleMailOutboundConfigs = useMemo(() => {
    const verifiedMailOutboundConfigs = mailOutboundConfigs.filter((config) => config.connectionVerified);

    if (!isTenant) {
      return verifiedMailOutboundConfigs;
    }

    if (!userId || !sharesLoaded) {
      return [];
    }

    const profileShare = shares.find((share) => share.applicationUserId === userId);
    const availableMailAccounts = profileShare?.availableMailAccounts || [];

    return verifiedMailOutboundConfigs.filter((config) => availableMailAccounts.includes(config.id));
  }, [isTenant, mailOutboundConfigs, shares, sharesLoaded, userId]);

  if (loading || (isTenant && loadingProfileShares && !sharesLoaded)) {
    return <Skeleton width="100%" height={200} />;
  }

  return (
    <>
      {visibleMailOutboundConfigs.map((cfg) => (
        <MailOutboundConfigDefaultFolderIMAP key={cfg.id} config={cfg} closeNavBar={closeNavBar} />
      ))}
    </>
  );
};

const MailOutboundConfigDefaultFolderIMAP: FC<{ config: MailOutBoundConfigDTO; closeNavBar: () => void }> = ({
  config,
  closeNavBar,
}) => {
  const { loadingFolder, defaultFolder } = useImapStore(config);
  const { email } = config;

  return (
    <>
      <Space h="md" />
      <NavigationLink
        defaultOpened
        compact
        label={
          <Text
            weight="bolder"
            sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {email}
          </Text>
        }
        icon={<InboxIcon size={20} />}
      >
        {loadingFolder && <Skeleton height={200} mx="sm" mb="xs" />}
        {defaultFolder && <DefaultFolder config={config} folder={defaultFolder} closeNavBar={closeNavBar} />}
        {!loadingFolder && defaultFolder === null && (
          <Group spacing="xs" mt="xs" pb="xs" noWrap>
            <IconPlugConnectedX size={16} color="#E65351" />
            <Text size="xs" color="red">
              Verbindung zum Postfach nicht moeglich.
            </Text>
          </Group>
        )}
      </NavigationLink>
    </>
  );
};

export default MailOutboundConfigNav;
